import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  ContentCopyOutlined,
  DeleteOutline,
  Edit,
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import SelectField from "../../atoms/SelectField/SelectField";
import ConfirmDialog from "../../atoms/ConfirmDialog/ConfirmDialog";
import {
  deleteCertificateTemplate,
  duplicateCertificateTemplate,
  findAllCertificateTemplates,
} from "../../../API/apiCertificateTemplates";
import { CertificateTemplateSummary } from "../../../types/CertificateTemplate";
import styles from "./ModelosCertificados.module.css";
import stylesFilter from "../../../styles/Filter.module.css";
import stylesInput from "../../../styles/Input.module.css";
import stylesTable from "../../../styles/Table.module.css";
import stylesButton from "../../../styles/Button.module.css";
import stylesGeneral from "../../../styles/General.module.css";

const ModelosCertificados = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<CertificateTemplateSummary[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingConfirmDialog, setLoadingConfirmDialog] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [searchName, setSearchName] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<CertificateTemplateSummary | null>(null);

  useEffect(() => {
    getTemplates();
  }, [currentPage, perPage]);

  useEffect(() => {
    if (!msgSuccess) return;
    const timer = setTimeout(() => setMsgSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSuccess]);

  useEffect(() => {
    if (!msgError) return;
    const timer = setTimeout(() => setMsgError(null), 5000);
    return () => clearTimeout(timer);
  }, [msgError]);

  const getTemplates = async () => {
    setLoading(true);
    try {
      const response = await findAllCertificateTemplates(currentPage, perPage);
      setTemplates(response.certificate_templates || []);
      setTotalPages(response.pagination?.last_page || 1);
    } catch (error) {
      setMsgError("Nao foi possivel listar os modelos de certificado.");
    } finally {
      setLoading(false);
    }
  };

  const displayedTemplates = useMemo(() => {
    return templates.filter((template) => {
      const matchName =
        !searchName ||
        template.name.toLowerCase().includes(searchName.trim().toLowerCase());
      const matchType = !filterType || template.documentType === filterType;
      const matchStatus =
        !filterStatus ||
        (filterStatus === "active" ? template.active : !template.active);

      return matchName && matchType && matchStatus;
    });
  }, [filterStatus, filterType, searchName, templates]);

  const handleOpenDelete = (template: CertificateTemplateSummary) => {
    setSelectedTemplate(template);
    setOpenDelete(true);
  };

  const handleOpenView = (template: CertificateTemplateSummary) => {
    setSelectedTemplate(template);
    setOpenViewDialog(true);
  };

  const handleOpenDuplicate = (template: CertificateTemplateSummary) => {
    setSelectedTemplate(template);
    setOpenDuplicateDialog(true);
  };

  const handleCloseConfirmDialogs = () => {
    setOpenDuplicateDialog(false);
    setOpenDelete(false);
    setLoadingConfirmDialog(false);
    setSelectedTemplate(null);
  };

  const handleDuplicate = async () => {
    if (!selectedTemplate) return;

    try {
      setLoadingConfirmDialog(true);
      await duplicateCertificateTemplate(selectedTemplate.id);
      setMsgSuccess("Modelo duplicado com sucesso.");
      setOpenDuplicateDialog(false);
      setSelectedTemplate(null);
      await getTemplates();
    } catch (error) {
      setOpenDuplicateDialog(false);
      setSelectedTemplate(null);
      setMsgError("Nao foi possivel duplicar o modelo.");
    } finally {
      setLoadingConfirmDialog(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;

    try {
      setLoadingConfirmDialog(true);
      await deleteCertificateTemplate(selectedTemplate.id);
      setMsgSuccess("Modelo excluido com sucesso.");
      setOpenDelete(false);
      setSelectedTemplate(null);
      await getTemplates();
    } catch (error) {
      setOpenDelete(false);
      setSelectedTemplate(null);
      setMsgError("Nao foi possivel excluir o modelo.");
    } finally {
      setLoadingConfirmDialog(false);
    }
  };

  const handleClearFilters = () => {
    setSearchName("");
    setFilterType("");
    setFilterStatus("");
  };

  const formatDate = (date: string | null) => {
    if (!date) return "-";

    try {
      return new Date(date).toLocaleString("pt-BR");
    } catch {
      return date;
    }
  };

  const getDocumentTypeLabel = (value: string) => {
    if (value === "certificate") return "Certificado";
    if (value === "declaration") return "Declaração";
    return "";
  };

  const getStatusLabel = (value: string) => {
    if (value === "active") return "Ativo";
    if (value === "inactive") return "Inativo";
    return "";
  };

  const handleChangeDocumentType = (value: string) => {
    if (value === "Certificado") {
      setFilterType("certificate");
      return;
    }

    if (value === "Declaracao") {
      setFilterType("declaration");
      return;
    }

    setFilterType("");
  };

  const handleChangeStatus = (value: string) => {
    if (value === "Ativo") {
      setFilterStatus("active");
      return;
    }

    if (value === "Inativo") {
      setFilterStatus("inactive");
      return;
    }

    setFilterStatus("");
  };

  return (
    <div className={styles.card}>
      <div>
        <div className={styles.titleRow}>
          <div className={styles.title}>Modelos de Certificados</div>
          <Button
            variant="contained"
            className={stylesButton.buttonSquarePrimary}
            onClick={() => navigate("/cadastrar-modelo-certificado")}
          >
            Cadastrar Novo Modelo
          </Button>
        </div>

        <div className={stylesFilter.container}>
          <div className={stylesFilter.grid12}>
            <div className={stylesFilter.inputGroup5}>
              <label className={stylesInput.titleInput}>Nome do modelo</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={searchName}
                onChange={(event) => setSearchName(event.target.value)}
                InputProps={{
                  className: stylesInput.input,
                  disableUnderline: true,
                }}
              />
            </div>

            <div className={stylesFilter.inputGroup4}>
              <label className={stylesInput.titleInput}>Tipo de documento</label>
              <SelectField
                label=""
                value={getDocumentTypeLabel(filterType)}
                onChange={handleChangeDocumentType}
                options={[
                  { nome: "Certificado" },
                  { nome: "Declaração" },
                ]}
                disableTyping
              />
            </div>

            <div className={stylesFilter.inputGroup3}>
              <label className={stylesInput.titleInput}>Status</label>
              <SelectField
                label=""
                value={getStatusLabel(filterStatus)}
                onChange={handleChangeStatus}
                options={[
                  { nome: "Ativo" },
                  { nome: "Inativo" },
                ]}
                disableTyping
              />
            </div>

            <div className={stylesFilter.inputGroupFull}>
              <div className={stylesFilter.containerButton}>
                <div className={stylesFilter.lineButton}>
                  <Button
                    variant="contained"
                    onClick={handleClearFilters}
                    className={stylesFilter.buttonClean}
                  >
                    Limpar
                  </Button>
                  <Button
                    variant="contained"
                    onClick={getTemplates}
                    className={stylesFilter.buttonSearch}
                  >
                    Atualizar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {msgSuccess && (
          <div className={stylesTable.message}>
            <Alert severity="success">{msgSuccess}</Alert>
          </div>
        )}

        {msgError && (
          <div className={stylesTable.message}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}

        <TableContainer className={stylesTable.tableContainer}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Table size="small">
              <TableHead className={stylesTable.tableHead}>
                <TableRow>
                  <TableCell padding="none" className={stylesTable.tableHeadRow} align="left">
                    Nome
                  </TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow} align="left">
                    Tipo
                  </TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow} align="left">
                    Pagina
                  </TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow} align="center">
                    Status
                  </TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow} align="left">
                    Atualizado em
                  </TableCell>
                  <TableCell padding="none" className={stylesTable.tableHeadRow} align="center">
                    Acoes
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {displayedTemplates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" className={stylesTable.textXs}>
                      Nenhum registro encontrado
                    </TableCell>
                  </TableRow>
                )}

                {displayedTemplates.map((template, index) => (
                  <TableRow
                    key={template.id}
                    className={`${stylesTable.tableRow} ${index % 2 === 0 ? stylesTable.tableRowEven : stylesTable.tableRowOdd}`}
                  >
                    <TableCell padding="none" className={stylesTable.textXs}>
                      {template.name}
                    </TableCell>
                    <TableCell padding="none" className={stylesTable.textXs}>
                      {template.documentTypeLabel}
                    </TableCell>
                    <TableCell padding="none" className={stylesTable.textXs}>
                      {template.page.size} / {template.page.orientation === "landscape" ? "Paisagem" : "Retrato"}
                    </TableCell>
                    <TableCell padding="none" className={stylesTable.textXs} align="center">
                      {template.active ? "Ativo" : "Inativo"}
                    </TableCell>
                    <TableCell padding="none" className={stylesTable.textXs}>
                      {formatDate(template.updated_at)}
                    </TableCell>
                    <TableCell padding="none">
                      <div className={stylesTable.blockActions}>
                        <Tooltip title="Editar modelo" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Editar modelo"
                            onClick={() => navigate(`/cadastrar-modelo-certificado?id=${template.id}`)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Visualizar turmas vinculadas" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Visualizar turmas vinculadas"
                            onClick={() => handleOpenView(template)}
                          >
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicar modelo" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Duplicar modelo"
                            onClick={() => handleOpenDuplicate(template)}
                          >
                            <ContentCopyOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir modelo" arrow placement="top">
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Excluir modelo"
                            onClick={() => handleOpenDelete(template)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {templates.length > 0 && (
          <div className={`${stylesTable.containerPagination} ${stylesGeneral.mt4}`}>
            <div className={stylesTable.qtdItems}>
              <div className={stylesTable.labelSelect}>Resultados por pagina</div>
              <SelectField
                label=""
                value={perPage.toString()}
                onChange={(value) => setPerPage(Number(value))}
                options={[{ nome: "10" }, { nome: "20" }, { nome: "30" }]}
                disableClearable
                disableTyping
              />
            </div>

            <div className={stylesTable.pagination}>
              <IconButton
                className={currentPage === 1 ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage}
                aria-label="Primeira pagina"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                <KeyboardDoubleArrowLeftOutlined />
              </IconButton>

              <IconButton
                className={currentPage === 1 ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage}
                aria-label="Pagina anterior"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <KeyboardArrowLeftOutlined />
              </IconButton>

              <span className={stylesTable.pages}>
                Pagina {currentPage} de {totalPages}
              </span>

              <IconButton
                className={currentPage === totalPages ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage}
                aria-label="Proxima pagina"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                <KeyboardArrowRightOutlined />
              </IconButton>

              <IconButton
                className={currentPage === totalPages ? stylesTable.iconButtonPageDisabled : stylesTable.iconButtonPage}
                aria-label="Ultima pagina"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                <KeyboardDoubleArrowRightOutlined />
              </IconButton>
            </div>

            <div className={stylesTable.setNumberPage}>
              <div className={stylesTable.labelSelect}>Ir para a pagina</div>
              <SelectField
                label=""
                value={currentPage.toString()}
                onChange={(value) => setCurrentPage(Number(value))}
                options={Array.from({ length: totalPages }, (_, index) => ({
                  nome: String(index + 1),
                }))}
                disableTyping
              />
            </div>
          </div>
        )}
      </div>

      <Dialog
        open={openViewDialog}
        onClose={() => {
          setOpenViewDialog(false);
          setSelectedTemplate(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Turmas vinculadas ao modelo</DialogTitle>
        <DialogContent dividers>
          <div className={styles.linkedClassesWrapper}>
            {selectedTemplate?.turmas?.length ? (
              selectedTemplate.turmas.map((turma) => (
                <div key={turma.id} className={styles.linkedClassCard}>
                  <div className={styles.linkedClassTitle}>{turma.nome}</div>
                  <div className={styles.linkedClassMeta}>
                    <span>Acao: {turma.acao_nome}</span>
                    <span>
                      Certificado obrigatorio para turma: {turma.obrigatoria_para_certificado ? "Sim" : "Nao"}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <Alert severity="info">Este modelo nao possui turmas vinculadas.</Alert>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            className={stylesButton.buttonSquareSecondary}
            onClick={() => {
              setOpenViewDialog(false);
              setSelectedTemplate(null);
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openDuplicateDialog}
        loading={loadingConfirmDialog}
        onClose={handleCloseConfirmDialogs}
        onConfirm={handleDuplicate}
        title="Duplicar modelo de certificado"
        message={`Deseja duplicar o modelo "${selectedTemplate?.name || ""}"?`}
        titleLoadButton="Duplicando..."
        titleButtonConfirm="Duplicar"
      />

      <ConfirmDialog
        open={openDelete}
        loading={loadingConfirmDialog}
        onClose={handleCloseConfirmDialogs}
        onConfirm={handleDelete}
        title="Excluir modelo de certificado"
        message={`Deseja realmente excluir o modelo "${selectedTemplate?.name || ""}"?`}
        titleLoadButton="Excluindo..."
        titleButtonConfirm="Excluir"
      />
    </div>
  );
};

export default ModelosCertificados;
